import React from 'react';
import { Chess } from 'chess.js';

const BestLineComponent = ({ analysis, currentFEN, showLabelAbove = false, userOrientation = 'white' }) => {
  const [isPlayingMoves, setIsPlayingMoves] = React.useState(false);
  const [isWaitingForAnalysis, setIsWaitingForAnalysis] = React.useState(false);
  const playbackTimeoutRef = React.useRef(null);
  const previousFENRef = React.useRef(currentFEN);
  const lastValidAnalysisRef = React.useRef(null);

  // Check if analysis matches current FEN
  const analysisMatchesFEN = React.useCallback((analysisData, fen) => {
    if (!analysisData || !fen) return false;
    
    // Check if the analysis has a FEN and it matches
    const analysisFEN = analysisData.positionFEN || analysisData.fen;
    if (!analysisFEN) return false;
    
    // Compare position part of FEN (excluding move counters)
    const currentPosition = fen.split(' ').slice(0, 4).join(' ');
    const analysisPosition = analysisFEN.split(' ').slice(0, 4).join(' ');
    
    return currentPosition === analysisPosition;
  }, []);

  // Handle individual move selection - dispatch event to board
  const handleMoveSelection = (moveData) => {
    if (moveData.bestMoveLAN || moveData.bestMoveSAN) {
      window.dispatchEvent(new CustomEvent('stockfish-move-selected', {
        detail: {
          move: moveData.bestMoveLAN || moveData.bestMoveSAN,
          san: moveData.bestMoveSAN,
          source: 'best-line-component'
        }
      }));
    }
  };


  // Handle sequential autoplay up to a specific move index
  const handleSequentialAutoplayTo = (targetMoveIndex) => {
    if (!analysis?.pvs?.[0]?.pv?.length) return;
    
    // Don't play if analysis doesn't match current FEN
    if (!analysisMatchesFEN(analysis, currentFEN)) {
      console.warn('BestLineComponent: Cannot play moves - analysis does not match current FEN');
      return;
    }
    
    const line = analysis.pvs[0];
    const movesToPlay = line.pv.slice(0, targetMoveIndex + 1); // Only play up to the clicked move

    // Clear any existing timeout
    if (playbackTimeoutRef.current) {
      clearTimeout(playbackTimeoutRef.current);
    }

    // Set playing state
    setIsPlayingMoves(true);
    setIsWaitingForAnalysis(true); // We'll need new analysis after moves

    // Play each move in sequence with delays
    movesToPlay.forEach((move, index) => {
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('stockfish-move-selected', {
          detail: {
            move: move,
            san: line.pvSAN?.[index] || move,
            source: 'best-line-autoplay',
            isSequential: true,
            moveIndex: index,
            totalMoves: movesToPlay.length,
            skipAnalysis: index < movesToPlay.length - 1 // Skip analysis for all but last move
          }
        }));

        // Clear playing state after last move
        if (index === movesToPlay.length - 1) {
          playbackTimeoutRef.current = setTimeout(() => {
            setIsPlayingMoves(false);
            // Keep waiting for analysis until we get valid data
          }, 100);
        }
      }, index * 500); // 500ms delay between moves
    });
  };
  // Helper function to format score display with POV normalization
  const formatScore = (analysis) => {
    if (!analysis?.pvs?.[0]) return null;
    
    const firstPv = analysis.pvs[0];
    
    // Determine if we need to flip the score based on board orientation
    // Engine evaluations are from White's perspective by default
    // Only flip if user is viewing from Black's perspective
    const shouldFlipScore = userOrientation === 'black';
    
    let scoreValue = null;
    let isMate = false;
    
    // The score is in the 'evaluation' field as a string (e.g., "+0.13", "-1.45", "#5")
    if (firstPv.evaluation) {
      const evalStr = firstPv.evaluation;
      
      // Handle mate scores (format: "#5" or "#-5" or "M5" or "M-5")
      if (evalStr.includes('#') || evalStr.toUpperCase().startsWith('M')) {
        isMate = true;
        // Remove # or M prefix to get the number
        const mateStr = evalStr.replace('#', '').replace('M', '').replace('m', '').trim();
        const mateNum = parseInt(mateStr);
        
        // Check if we got a valid number
        if (!isNaN(mateNum)) {
          const normalizedMate = shouldFlipScore ? -mateNum : mateNum;
          return {
            text: normalizedMate > 0 ? `M${Math.abs(normalizedMate)}` : `-M${Math.abs(normalizedMate)}`,
            value: normalizedMate,
            isMate: true
          };
        }
      }
      
      // Handle regular scores
      const numValue = parseFloat(evalStr);
      if (!isNaN(numValue)) {
        scoreValue = shouldFlipScore ? -numValue : numValue;
      }
    }
    
    // Alternative: use normalizedCP if evaluation string is not available
    if (scoreValue === null && firstPv.normalizedCP !== undefined && firstPv.normalizedCP !== null) {
      scoreValue = shouldFlipScore ? -firstPv.normalizedCP : firstPv.normalizedCP;
    }
    
    if (scoreValue !== null) {
      const formatted = scoreValue.toFixed(1);
      return {
        text: scoreValue > 0 ? `+${formatted}` : formatted,
        value: scoreValue,
        isMate: false
      };
    }
    
    return null;
  };

  // Helper function to parse FEN and get move context
  const getFENMoveContext = (fenString) => {
    if (!fenString) return { activeColor: 'w', fullMoveNumber: 1 };
    
    const fenParts = fenString.split(' ');
    const activeColor = fenParts[1] || 'w'; // 'w' or 'b'
    const fullMoveNumber = parseInt(fenParts[5]) || 1;
    
    return { activeColor, fullMoveNumber };
  };

  // Format entire PV line with move numbers - returns array of move objects
  const formatPVLineWithNumbers = (line, fenString) => {
    // Validate inputs
    if (!line || !fenString) {
      console.warn('BestLineComponent: Invalid inputs to formatPVLineWithNumbers');
      return [];
    }

    // Get the SAN moves array
    let sanMoves = [];
    if (line?.pvSAN && line.pvSAN.length > 0) {
      sanMoves = line.pvSAN;
    } else if (line?.pv && line.pv.length > 0) {
      // Try to convert LAN to SAN
      try {
        const chess = new Chess(fenString);
        for (let i = 0; i < Math.min(7, line.pv.length); i++) { // Max 7 moves
          const move = chess.move(line.pv[i]);
          if (move) {
            sanMoves.push(move.san);
          } else {
            console.warn(`BestLineComponent: Could not convert move ${line.pv[i]} at index ${i}`);
            break;
          }
        }
      } catch (error) {
        console.warn('BestLineComponent: Error converting LAN to SAN:', error);
        // Fallback - return moves as simple array (max 7)
        return line.pv.slice(0, 7
          
        ).map((move, index) => ({
          text: move,
          index: index,
          isMove: true
        }));
      }
    }
    
    if (sanMoves.length === 0) return [];
    
    const { activeColor } = getFENMoveContext(fenString);
    let result = [];
    let currentMoveNum = 1; // Always start at 1
    let isWhiteToMove = activeColor === 'w';
    let isFirstMove = true;
    
    for (let i = 0; i < Math.min(7, sanMoves.length); i++) { // Max 7 moves
      if (isWhiteToMove) {
        // Add move number before white's move
        result.push({
          text: `${currentMoveNum}.`,
          index: i,
          isMove: false
        });
        result.push({
          text: sanMoves[i],
          index: i,
          isMove: true
        });
        isWhiteToMove = false;
      } else {
        // Black's move - add ellipsis for first move only
        if (isFirstMove) {
          result.push({
            text: `${currentMoveNum}...`,
            index: i,
            isMove: false
          });
        }
        result.push({
          text: sanMoves[i],
          index: i,
          isMove: true
        });
        isWhiteToMove = true;
        currentMoveNum++;
      }
      isFirstMove = false;
    }
    
    return result;
  };

  // Track FEN changes and validate analysis
  React.useEffect(() => {
    // Check if FEN has changed
    if (previousFENRef.current !== currentFEN) {
      // console.log('BestLineComponent: FEN changed, marking as waiting for analysis');
      
      // FEN changed, we need new analysis
      setIsWaitingForAnalysis(true);
      previousFENRef.current = currentFEN;
      
      // Clear any playing state
      setIsPlayingMoves(false);
      if (playbackTimeoutRef.current) {
        clearTimeout(playbackTimeoutRef.current);
      }
    }
    
    // Check if we have valid analysis for current FEN
    if (analysis && currentFEN) {
      const isValid = analysisMatchesFEN(analysis, currentFEN);
      
      if (isValid) {
        // console.log('BestLineComponent: Valid analysis received for current FEN');
        setIsWaitingForAnalysis(false);
        lastValidAnalysisRef.current = analysis;
      } else {
        // console.log('BestLineComponent: Analysis does not match current FEN, waiting...');
        setIsWaitingForAnalysis(true);
      }
    }
  }, [analysis, currentFEN, analysisMatchesFEN]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (playbackTimeoutRef.current) {
        clearTimeout(playbackTimeoutRef.current);
      }
    };
  }, []);

  // Debug what data we're receiving (remove this later)
  // console.log('BestLineComponent debug:', {
  //   hasAnalysis: !!analysis,
  //   hasPvs: !!analysis?.pvs,
  //   hasFirstPv: !!analysis?.pvs?.[0],
  //   hasFEN: !!currentFEN,
  //   analysis: analysis,
  //   currentFEN: currentFEN
  // });

  // Don't render if no analysis data - show calculating message
  if (!analysis || !analysis.pvs || !analysis.pvs[0] || !currentFEN) {
    return (
      <div style={{
        padding: '4px 6px',
        backgroundColor: '#333',
        borderRadius: '4px',
        color: '#ffd700',
        fontSize: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
      }}>
        <i className="fa-solid fa-chess-knight fa-bounce" style={{ color: '#ffd700', fontWeight: 'bold' }}></i>
        <span>Calculating...</span>
      </div>
    );
  }

  // Handle first move click
  const handleFirstMoveClick = () => {
    const firstMove = analysis.pvs[0];
    if (firstMove) {
      handleMoveSelection(firstMove);
    }
  };

  // Render with label above (ViewGame style)
  if (showLabelAbove) {
    return (
      <div style={{
        padding: '0px 6px',
        backgroundColor: '',
        borderRadius: '4px',
        color: '#ccc',
      }}>
        {/* Best Line label on separate row with score */}
        <div style={{ 
          marginBottom: '8px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span 
            style={{ 
              color: '#ffd700', 
              fontWeight: 'bold',
              fontSize: '16px',
              cursor: 'pointer'
            }}
            onClick={handleFirstMoveClick}
            title="Click to play first move"
          >
            Best Engine Line:
          </span>
          {(() => {
            const scoreData = formatScore(analysis);
            if (!scoreData) return null;
            
            // Determine colors based on normalized score value
            let bgColor, textColor;
            if (scoreData.isMate) {
              // Mate scores
              bgColor = scoreData.value > 0 ? '#4CAF50' : '#f44336';
              textColor = scoreData.value > 0 ? 'black' : 'white';
            } else {
              // Regular scores
              if (Math.abs(scoreData.value) < 0.15) {
                // Nearly equal position
                bgColor = '#666';
                textColor = 'white';
              } else {
                bgColor = scoreData.value > 0 ? '#4CAF50' : '#f44336';
                textColor = scoreData.value > 0 ? 'black' : 'white';
              }
            }
            
            return (
              <span style={{
                backgroundColor: bgColor,
                color: textColor,
                fontSize: '14px',
                fontWeight: 'bold',
                fontFamily: 'monospace',
                padding: '2px 8px',
                borderRadius: '4px'
              }}>
                {scoreData.text}
              </span>
            );
          })()}
        </div>
        
        {/* PV data on separate row */}
        <div style={{ 
          display: 'flex', 
          flexWrap: 'nowrap', 
          gap: '4px',
          overflow: 'hidden',
          alignItems: 'center'
        }}>
          {isPlayingMoves ? (
            <span style={{ color: '#ffd700', fontSize: '16px' }}>
              Playing moves...
            </span>
          ) : isWaitingForAnalysis || !analysisMatchesFEN(analysis, currentFEN) ? (
            <span style={{ color: '#ffd700', fontSize: '16px' }}>
              Calculating...
            </span>
          ) : !analysis.pvs[0]?.pvSAN && !analysis.pvs[0]?.pv ? (
            <span style={{ color: '#ffd700', fontSize: '16px' }}>
              Calculating...
            </span>
          ) : (() => {
            const formattedMoves = formatPVLineWithNumbers(analysis.pvs[0], currentFEN);
            const moveCount = analysis.pvs[0]?.pvSAN?.length || analysis.pvs[0]?.pv?.length || 0;
            
            return (
              <>
                {formattedMoves.map((item, idx) => (
                  item.isMove ? (
                    <span
                      key={idx}
                      style={{ 
                        color: '#ccc',
                        fontSize: '16px',
                        cursor: 'pointer',
                        padding: '2px 4px',
                        backgroundColor: '#333333',
                        borderRadius: '4px',
                        transition: 'background-color 0.2s',
                        flexShrink: 0
                      }}
                      onClick={() => handleSequentialAutoplayTo(item.index)}
                      onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 215, 0, 0.3)'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
                      title={`Play moves 1-${item.index + 1}`}
                    >
                      {item.text}
                    </span>
                  ) : (
                    <span
                      key={idx}
                      style={{ 
                        color: '#888',
                        fontSize: '16px',
                        padding: '2px 2px',
                        flexShrink: 0
                      }}
                    >
                      {item.text}
                    </span>
                  )
                ))}
                {moveCount > 7 && (
                  <span style={{ color: '#888', fontSize: '16px', flexShrink: 0 }}>...</span>
                )}
              </>
            );
          })()}
        </div>
      </div>
    );
  }

  // Render with label inline (MobileMoves Stockfish tab style)
  return (
    <div style={{
      padding: '2px',
      // backgroundColor: '',
      borderRadius: '4px',
      color: '#ccc',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      flexWrap: 'wrap',
      overflowX: 'auto'
    }}>
      <span 
        style={{ 
          color: '#ffd700', 
          fontWeight: 'bold',
          fontSize: '16px',
          whiteSpace: 'nowrap',
          cursor: 'pointer'
        }}
        onClick={handleFirstMoveClick}
        title="Click to play first move"
      >
        Best Line:
      </span>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '4px', 
        flexWrap: 'nowrap',
        overflow: 'hidden',
        flex: 1
      }}>
        {isPlayingMoves ? (
          <span style={{ color: '#ffd700', fontSize: '16px' }}>
            Playing moves...
          </span>
        ) : isWaitingForAnalysis || !analysisMatchesFEN(analysis, currentFEN) ? (
          <span style={{ color: '#ffd700', fontSize: '16px' }}>
            Calculating...
          </span>
        ) : !analysis.pvs[0]?.pvSAN && !analysis.pvs[0]?.pv ? (
          <span style={{ color: '#ffd700', fontSize: '16px' }}>
            Calculating...
          </span>
        ) : (() => {
          const formattedMoves = formatPVLineWithNumbers(analysis.pvs[0], currentFEN);
          const moveCount = analysis.pvs[0]?.pvSAN?.length || analysis.pvs[0]?.pv?.length || 0;
          
          return (
            <>
              {formattedMoves.map((item, idx) => (
                item.isMove ? (
                  <span
                    key={idx}
                    style={{ 
                      color: '#fff',
                      fontSize: '16px',
                      cursor: 'pointer',
                      padding: '2px 4px',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: '4px',
                      transition: 'background-color 0.2s',
                      flexShrink: 0
                    }}
                    onClick={() => handleSequentialAutoplayTo(item.index)}
                    onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 215, 0, 0.3)'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
                    title={`Play moves 1-${item.index + 1}`}
                  >
                    {item.text}
                  </span>
                ) : (
                  <span
                    key={idx}
                    style={{ 
                      color: '#888',
                      fontSize: '16px',
                      flexShrink: 0
                    }}
                  >
                    {item.text}
                  </span>
                )
              ))}
              {moveCount > 7 && (
                <span style={{ color: '#888', fontSize: '16px', flexShrink: 0 }}>...</span>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
};

export default BestLineComponent;